import { createContext, useContext, useEffect } from 'react'
import { useQuery } from 'apollo-client'
import localStorage from 'local-storage'
import type { UserGender } from 'typings/graphql'

import userQuery, { type UserPayload } from './graph/user.graphql'


// make it compatible with ts
type PersonalInfo = UserPayload['currentUser']['data']['personalInfo']
type ReferralInvites = UserPayload['currentUser']['data']['referralInvites']

export type ModifiedUser = PersonalInfo & {
  addressInfo: PersonalInfo['addressInfo'] & {
    shipping: Required<PersonalInfo['addressInfo']['shipping']>
    billing: Required<PersonalInfo['addressInfo']['billing']>
  }
}

export type UserContextValue = {
  user: ModifiedUser
  userInvites: ReferralInvites
  isLoggedIn: boolean
  gender: UserGender
  hasBillingAddress: boolean
  hasScentProfile: boolean
  isFetching: boolean
}

const UserContext = createContext<UserContextValue>(null)

export const UserProvider: React.CFC = ({ children }) => {
  const { data, isFetching } = useQuery(userQuery, {
    fetchPolicy: 'cache-first',
  })

  const user = data?.currentUser?.data?.personalInfo as ModifiedUser
  const userInvites = data?.currentUser?.data?.referralInvites

  const isLoggedIn = user !== null
  const gender: UserGender = user ? user.gender : 'FEMALE'
  const hasBillingAddress = Boolean(user?.addressInfo?.billing)
  const hasScentProfile = Boolean(user?.analyticsMetadata?.hasScentProfile)

  const tokenApi = user?.tokenApi

  useEffect(() => {
    // set token for WS connection
    if (tokenApi) {
      localStorage.setSessionItem('tokenApi', tokenApi)
    }
    else {
      localStorage.removeSessionItem('tokenApi')
    }
  }, [ tokenApi ])

  const value: UserContextValue = {
    user,
    userInvites,
    isLoggedIn,
    gender,
    hasBillingAddress,
    hasScentProfile,
    isFetching,
  }

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  )
}

export const useUser = () => useContext(UserContext)
